import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import type { DecompositionNode } from 'api/data/projects/types';
import { normalizeId } from 'utils/ids';

import type { DecompositionTreePage, TreeCacheScope } from '../types';
import { mapMatchingNodesInPages } from './nodeListMutations';
import { removeNodeFromParentChildrenPages } from './paginatedCacheMutations';
import { removeFromStickyTop } from './stickyTopCacheMutations';
import { nodeMatchesId } from './treeCacheScope';
import { treeQueryKeys } from './treeQueryKeys';

/**
 * Reads and writes that span BOTH tree caches — the paginated lists
 * (`paginatedCacheMutations.ts`) and the sticky-top lists (`stickyTopCacheMutations.ts`), which are
 * separate query-cache namespaces. A node's presence has to stay consistent across the two, so new
 * call sites should compose from here rather than touching one side directly.
 */

type PagesCache = InfiniteData<DecompositionTreePage, number>;

/** Visits every copy of a node in the scope; a visitor returning `true` stops the walk. */
function forEachNodeInCache(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  normalId: string,
  visitor: (node: DecompositionNode) => boolean | void
): void {
  const pageLists = queryClient
    .getQueriesData<PagesCache>({ predicate: treeQueryKeys.pages(scope) })
    .flatMap(([, data]) => data?.pages.map((page) => page.items) ?? []);

  const stickyLists = queryClient
    .getQueriesData<DecompositionNode[]>({ predicate: treeQueryKeys.sticky(scope) })
    .map(([, list]) => list ?? []);

  for (const list of [...pageLists, ...stickyLists]) {
    for (const node of list) {
      if (!nodeMatchesId(node, normalId)) continue;
      if (visitor(node) === true) return;
    }
  }
}

export function findNodeInCache(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  nodeId: string
): DecompositionNode | undefined {
  let found: DecompositionNode | undefined;
  forEachNodeInCache(queryClient, scope, normalizeId(nodeId), (node) => {
    found = node;
    return true;
  });
  return found;
}

/** Every cache placement of a node — paginated lists and sticky-top lists alike. */
export function collectNodesInCache(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  nodeId: string
): DecompositionNode[] {
  const results: DecompositionNode[] = [];
  forEachNodeInCache(queryClient, scope, normalizeId(nodeId), (node) => {
    results.push(node);
  });
  return results;
}

/** Sets `hasChildren` on a node in every list of the scope that holds it (defaults to `true`). */
export function patchHasChildren(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  nodeId: string,
  hasChildren = true
): void {
  const normalId = normalizeId(nodeId);
  // Match only nodes whose flag actually differs, so a redundant patch returns the same reference
  // and react-query skips the update — this handler fires several times per add (the optimistic
  // add, DataNodeCreated and DataRelationCreated all patch the same parent).
  const needsFlag = (node: DecompositionNode) => nodeMatchesId(node, normalId) && node.hasChildren !== hasChildren;
  const setFlag = (node: DecompositionNode) => ({ ...node, hasChildren });

  queryClient.setQueriesData<PagesCache>({ predicate: treeQueryKeys.pages(scope) }, (prev) =>
    mapMatchingNodesInPages(prev, needsFlag, setFlag)
  );

  queryClient.setQueriesData<DecompositionNode[]>({ predicate: treeQueryKeys.sticky(scope) }, (prev) =>
    !prev || !prev.some(needsFlag) ? prev : prev.map((node) => (needsFlag(node) ? setFlag(node) : node))
  );
}

/**
 * What the cache can say about a parent's children: it holds some, it provably holds none, or it
 * has never been told (a collapsed parent whose children were never fetched).
 */
type ChildrenCacheState = 'has-children' | 'empty' | 'unknown';

function childrenCacheState(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  parentDataNodeId: string
): ChildrenCacheState {
  const pinned = queryClient.getQueryData<DecompositionNode[]>(treeQueryKeys.stickyTop(scope, parentDataNodeId));
  if (pinned && pinned.length > 0) return 'has-children';

  const children = queryClient.getQueryData<PagesCache>(treeQueryKeys.children(scope, parentDataNodeId));
  if (!children) return 'unknown';
  return children.pages.some((page) => page.items.length > 0 || page.hasMore) ? 'has-children' : 'empty';
}

/**
 * True when the node was created during this session, as recorded by `buildStickyNode`
 * (treeLiveSync.ts). Every child such a node has ever had arrived as a live event we handled —
 * nothing could have changed its children before it existed — so its sticky-top list is a gap-free
 * record, and an empty one really does mean zero children. An ordinary node's empty sticky list
 * proves nothing, since its children page may simply never have been fetched.
 *
 * The marker rides on the pinned copy, so it is dropped along with the pin when the tree page is
 * left. `onRelationCreated` also clears it deliberately when it can't record a child it just
 * gained — one gap is enough to make the list unusable as proof of emptiness.
 */
export function isNodeCreatedThisSession(queryClient: QueryClient, scope: TreeCacheScope, nodeId: string): boolean {
  const normalId = normalizeId(nodeId);

  return queryClient
    .getQueriesData<DecompositionNode[]>({ predicate: treeQueryKeys.sticky(scope) })
    .some(([, list]) => list?.some((node) => nodeMatchesId(node, normalId) && node.createdThisSession === true));
}

/**
 * After a child is detached, clears the parent's `hasChildren` flag when the cache can prove the
 * parent now has zero children — either its children page is loaded and empty, or it was created
 * this session (see `isNodeCreatedThisSession`).
 *
 * A collapsed parent whose children were never loaded has no local record, so nothing here can
 * prove it either way. Its flag is deliberately left untouched: it keeps the last server-derived
 * value rather than a guess, and the stale expand arrow resolves itself when the user opens it
 * (the loaded-but-empty children list leaves the item with no children — see useTreeItems).
 */
export function resetHasChildrenIfEmpty(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  parentDataNodeId: string
): void {
  const state = childrenCacheState(queryClient, scope, parentDataNodeId);
  if (state === 'has-children') return;
  if (state === 'empty' || isNodeCreatedThisSession(queryClient, scope, parentDataNodeId)) {
    patchHasChildren(queryClient, scope, parentDataNodeId, false);
  }
}

/** Drops a child from one parent, on both sides of the cache. */
export function detachNodeFromParentCache(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  parentDataNodeId: string,
  nodeId: string
): void {
  removeFromStickyTop(queryClient, scope, parentDataNodeId, nodeId);
  removeNodeFromParentChildrenPages(queryClient, scope, parentDataNodeId, nodeId);
}
